import React, { useState } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UploadedFile } from './index';
import { handlePreviewFile } from 'src/services/common';

interface FileUploadFormProps {
  uploadedFiles: UploadedFile[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;

  handleFileUpload: (index: number, file: File) => Promise<void>;
  addFileUpload: () => void;
  removeFileUpload: (index: number) => void;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const FileUploadForm: React.FC<FileUploadFormProps> = ({
  uploadedFiles,
  setUploadedFiles,
  handleFileUpload,
  addFileUpload,
  removeFileUpload,
  setError
}) => {
  const [dragActive, setDragActive] = useState<{ [key: number]: boolean }>({});

  const hasUploadedFile = uploadedFiles.some(
    (file) => file.uploaded || file.file
  );

  const handleRemoveDocument = (index: number) => {
    // If it's not the first row, remove the entire row
    if (index > 0) {
      removeFileUpload(index);
    } else {
      // For the first row, just clear the file data but keep the entry
      const newFiles = [...uploadedFiles];

      newFiles[index] = {
        ...newFiles[index],
        file: null,
        fileUrl: null,
        fileName: '',
        uploaded: false,
        uploadError: null,
        id: '',
        fileType: ''
      };

      setUploadedFiles(newFiles);
    }
  };

  // File validation function
  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedExtensions = [
      '.pdf',
      '.xls',
      '.xlsx',
      '.doc',
      '.docx',
      '.zip'
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return `Invalid file type. Only ${allowedExtensions.join(
        ', '
      )} files are allowed.`;
    }

    // Validate file size (max 10MB)
    const maxSize = 200 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return `File is too large. Maximum file size is 200MB.`;
    }

    return null;
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive((prev) => ({ ...prev, [index]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive((prev) => ({ ...prev, [index]: false }));
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [index]: false }));

    if (uploadedFiles[index].isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const selectedFile = files[0];
      const validationError = validateFile(selectedFile);

      if (validationError) {
        // Update the file entry with the error
        // const newFiles = [...uploadedFiles];
        // newFiles[index] = {
        //   ...newFiles[index],
        //   file: selectedFile,
        //   uploaded: false,
        //   uploadError: validationError
        // };
        // setUploadedFiles(newFiles);

        // Set global error

        const newFiles = [...uploadedFiles];
        newFiles[index] = {
          ...newFiles[index],
          file: null,
          title: 'Bid Document',
          uploaded: false
        };
        setUploadedFiles(newFiles);
        setError(validationError);
      } else {
        // Proceed with upload if validation passes
        handleFileUpload(index, selectedFile);
        setError(null);
      }
    }
  };

  // Handle file selection from input
  const handleFileSelect = (index: number, selectedFile: File) => {
    const validationError = validateFile(selectedFile);

    if (validationError) {
      // Update the file entry with the error
      // const newFiles = [...uploadedFiles];
      // newFiles[index] = {
      //   ...newFiles[index],
      //   file: selectedFile,
      //   uploaded: false,
      //   uploadError: validationError
      // };
      // setUploadedFiles(newFiles);

      const newFiles = [...uploadedFiles];
      newFiles[index] = {
        ...newFiles[index],
        file: null,
        title: 'Bid Document',
        uploaded: false
      };
      setUploadedFiles(newFiles);

      // Set global error if needed
      setError(validationError);
    } else {
      // Proceed with upload if validation passes
      handleFileUpload(index, selectedFile);
      setError(null);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        You can only upload files with extension -
        .pdf/.xls/.xlsx/.doc/.docx/.zip (Max size: 10MB)
      </Alert>

      {!hasUploadedFile && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          At least one document must be uploaded before proceeding
        </Alert>
      )}

      {uploadedFiles.map((file, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          {/* <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="File Title"
              value={file.fileName}
              onChange={(e) => {
                const newFiles = [...uploadedFiles];
                newFiles[index].title = e.target.value;
                setUploadedFiles(newFiles);
              }}
              disabled
              InputLabelProps={{
                shrink: true
              }}
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid> */}
          <Grid item xs={12} sm={6}>
            {file.isUploading ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2">Uploading...</Typography>
              </Box>
            ) : (
              <>
                {/* Drag & Drop Zone */}
                <Paper
                  onDragEnter={(e) => handleDrag(e, index)}
                  onDragLeave={(e) => handleDrag(e, index)}
                  onDragOver={(e) => handleDrag(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: dragActive[index]
                      ? '2px dashed #1976d2'
                      : file.uploadError
                      ? '2px dashed #d32f2f'
                      : file.fileUrl
                      ? '2px dashed #2e7d32'
                      : '2px dashed #ccc',
                    backgroundColor: dragActive[index]
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'transparent',
                    borderRadius: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <input
                    type="file"
                    hidden
                    id={`file-input-${index}`}
                    accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileSelect(index, e.target.files[0]);
                      }
                    }}
                  />

                  <label
                    htmlFor={`file-input-${index}`}
                    style={{ cursor: 'pointer', display: 'block' }}
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 40,
                        color: file.uploadError
                          ? 'error.main'
                          : file.uploaded
                          ? 'success.main'
                          : 'primary.main',
                        mb: 1
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {file.fileUrl
                        ? 'Drop file to replace or click to browse'
                        : 'Drag & drop file here or click to browse'}
                    </Typography>
                  </label>

                  {file.file && (
                    <Typography
                      variant="caption"
                      display="block"
                      color={file.uploadError ? 'error' : 'textSecondary'}
                      sx={{
                        mt: 1,
                        fontWeight: 'medium',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        maxWidth: '100%'
                      }}
                    >
                      📄 {file.file.name}
                      {file.uploadError && ` - ${file.uploadError}`}
                    </Typography>
                  )}

                  {file.fileUrl && !file.file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color={file.uploaded ? 'success.main' : 'textSecondary'}
                        sx={{ fontWeight: 'medium' }}
                      >
                        ✅{' '}
                        {file.uploaded
                          ? 'Successfully uploaded: '
                          : 'Existing file: '}
                        {file.title || file.fileName}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            sm={2}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            {/* Action buttons based on state */}
            {!file.isUploading && (
              <>
                {file.fileUrl ? (
                  /* If a file is uploaded, show view and remove buttons */
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => handlePreviewFile(file.fileUrl)}
                      sx={{
                        borderRadius: '20px',
                        minWidth: '36px',
                        padding: '6px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleRemoveDocument(index)}
                      sx={{
                        borderRadius: '20px',
                        minWidth: '36px',
                        padding: '6px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      {index > 0 ? 'Remove' : 'Remove'}
                    </Button>
                  </Box>
                ) : (
                  /* If no file but not the first row, show remove row button */
                  index > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => removeFileUpload(index)}
                      sx={{
                        borderRadius: '20px',
                        minWidth: '36px',
                        padding: '6px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      Remove
                    </Button>
                  )
                )}
              </>
            )}
          </Grid>

          {file.uploadError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>
                {file.uploadError}
              </Alert>
            </Grid>
          )}

          {file.uploaded && (
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mt: 1 }}>
                File uploaded successfully
              </Alert>
            </Grid>
          )}
        </Grid>
      ))}

      <Button variant="outlined" onClick={addFileUpload} sx={{ mt: 2 }}>
        Add Another File
      </Button>

      <Typography
        variant="body2"
        sx={{ mt: 4, color: 'text.secondary', fontWeight: 'bold' }}
      >
        Note: ⏳ Uploading large files may take some time to complete.
      </Typography>
    </Box>
  );
};

export default FileUploadForm;
